import { createContext, useContext, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { User, onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/lib/firebase/config';
import { signInWithGoogle, signInWithGoogleRedirect, signOut as firebaseSignOut } from '@/lib/firebase/auth';
import { updateDocument, subscribeToDocument, DocumentData } from '@/lib/firebase/firestore';

interface UserProfile extends DocumentData {
  full_name?: string;
  phone?: string;
  profile_completed?: boolean;
}

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  updatePhone: (phone: string) => Promise<{ error: unknown | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        // Subscribe to user profile changes
        const unsubscribeProfile = subscribeToDocument<UserProfile>(
          'users',
          currentUser.uid,
          (data) => {
            setProfile(data);
            setLoading(false);
          }
        );

        return () => {
          unsubscribeProfile();
        };
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const handleSignInWithGoogle = async () => {
    try {
      const isNative = Capacitor.isNativePlatform?.() === true;

      if (isNative) {
        // For mobile apps, use redirect flow
        await signInWithGoogleRedirect();
      } else {
        // For web, use popup flow
        await signInWithGoogle();
      }
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const handleSignOut = async () => {
    try {
      await firebaseSignOut();
      setUser(null);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const updatePhone = async (phone: string) => {
    try {
      if (!user) throw new Error('No user logged in');

      // In Firebase Auth, updating phone number requires re-verification 
      // via SMS, which is a complex flow. For now, we'll just update
      // the phone number in the user's Firestore document.

      // Update in users collection
      await updateDocument('users', user.uid, {
        phone,
        updatedAt: new Date(),
      });

      return { error: null };
    } catch (error) {
      console.error('Error updating phone:', error);
      return { error };
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      loading,
      signInWithGoogle: handleSignInWithGoogle,
      signOut: handleSignOut,
      updatePhone
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

